/**
 * התראות Push (Web Push) — טיפוסים משותפים לשרת ולדפדפן.
 * מכשיר אחד (דפדפן) מחזיק מנוי Push אחד בלבד לכל אתר, ולכן מנוי יכול להיות רשום גם כשחקן וגם כמנהל.
 */

export type PushRole = 'player' | 'admin';

export type PushKind =
  | 'message'
  | 'mission'
  | 'solution-reviewed'
  | 'solution-submitted'
  | 'player-message'
  | 'test';

/** תוכן ההתראה כפי שנשלח למכשיר (מוצפן בדרך). לעולם לא כולל תוכן של משימה שטרם נפתחה */
export interface PushPayload {
  kind: PushKind;
  title: string;
  body: string;
  /** נתיב יחסי שייפתח בלחיצה על ההתראה */
  url: string;
  /** התראות עם אותו tag מחליפות זו את זו במקום להיערם */
  tag: string;
  /** דחופה: נשארת על המסך עד שמסירים אותה (איפה שנתמך) ורוטטת חזק */
  urgent?: boolean;
  /** מתי נשלחה (ms) — מוצג בהתראה, כדי שיהיה ברור כמה היא טרייה */
  sentAt?: number;
}

/** אפשרויות מסירה של התראה: תוקף (אחרי כמה שניות שירות ה-Push מוותר על מסירה) */
export interface PushDelivery {
  ttlSeconds: number;
}

/** תוקף התראה רגילה: יום. המכשיר יכול להיות כבוי כמה שעות, וההתראה עדיין נכונה כשהוא חוזר */
export const DEFAULT_PUSH_TTL_SECONDS = 24 * 60 * 60;

// ===== ניסוח ההתראות לשחקנים (נקבע על ידי המנהל) =====

/** אירועים שמחוללים התראה לשחקן וניתן לנסח אותם בהגדרות */
export const NOTIFY_EVENT_KEYS = [
  'mission-opened',
  'admin-message',
  'solution-approved',
  'solution-approved-waiting',
  'solution-rejected',
] as const;

export type NotifyEventKey = (typeof NOTIFY_EVENT_KEYS)[number];

export interface NotifyTemplate {
  title: string;
  body: string;
  /** דחופה: עדיפות מסירה גבוהה, נשארת על המסך עד שמסירים אותה (איפה שנתמך), רטט חזק, ומסומנת 🚨 בכותרת */
  urgent: boolean;
  /** רגישה בזמן: אם המכשיר לא קיבל אותה תוך ttlMinutes היא נמחקת במקום להגיע באיחור, ומסומנת ⏰ בכותרת */
  timeSensitive: boolean;
  ttlMinutes: number;
}

export type NotifyTemplates = Record<NotifyEventKey, NotifyTemplate>;

export interface NotifyEventInfo {
  key: NotifyEventKey;
  label: string;
  /** מתי נשלחת ההתראה */
  when: string;
  /** שמות ה-placeholders שמותר לכתוב בתבנית, בלי הסוגריים המסולסלים */
  placeholders: string[];
}

export const NOTIFY_EVENTS: NotifyEventInfo[] = [
  {
    key: 'mission-opened',
    label: 'משימה נפתחה',
    when: 'כשמשימה נפתחת בזמן/על ידי המנהל (לא כשהשחקן עצמו פתח אותה במכשיר)',
    placeholders: ['משימה', 'מספר', 'שם המבצע'],
  },
  {
    key: 'admin-message',
    label: 'הודעה מהמנהל',
    when: 'כשנשלחת הודעה לשחקן ממסך ההודעות (שם אפשר לשנות את הניסוח והדחיפות לכל הודעה בנפרד)',
    placeholders: ['הודעה', 'שם המבצע'],
  },
  {
    key: 'solution-approved',
    label: 'פתרון אושר — נפתחה המשימה הבאה',
    when: 'כשפתרון אושר (ידנית או אוטומטית) והמשימה הבאה נפתחה',
    placeholders: ['שם המבצע'],
  },
  {
    key: 'solution-approved-waiting',
    label: 'פתרון אושר — המשימה הבאה עוד לא מוכנה',
    when: 'כשפתרון אושר אבל אין עדיין משימה באה לפתוח',
    placeholders: ['שם המבצע'],
  },
  {
    key: 'solution-rejected',
    label: 'פתרון נדחה',
    when: 'כשהמנהל דוחה פתרון',
    placeholders: ['הערה', 'שם המבצע'],
  },
];

export const DEFAULT_NOTIFY_TEMPLATES: NotifyTemplates = {
  'mission-opened': {
    title: '{שם המבצע}',
    body: '{משימה} נפתחה — היכנסו לאפליקציה',
    urgent: true,
    timeSensitive: true,
    ttlMinutes: 30,
  },
  'admin-message': {
    title: 'הודעה חדשה מהמפקדה',
    body: '{הודעה}',
    urgent: true,
    timeSensitive: true,
    ttlMinutes: 30,
  },
  'solution-approved': {
    title: 'הפתרון אושר ✓',
    body: 'משימה חדשה נפתחה — היכנסו לאפליקציה',
    urgent: true,
    timeSensitive: true,
    ttlMinutes: 30,
  },
  'solution-approved-waiting': {
    title: 'הפתרון אושר ✓',
    body: 'המשימה הבאה תגיע בקרוב',
    urgent: false,
    timeSensitive: false,
    ttlMinutes: 30,
  },
  'solution-rejected': {
    title: 'הפתרון לא התקבל',
    body: '{הערה}',
    urgent: false,
    timeSensitive: true,
    ttlMinutes: 60,
  },
};

export const NOTIFY_TITLE_MAX = 80;
export const NOTIFY_BODY_MAX = 300;
export const NOTIFY_TTL_MIN_MINUTES = 1;
export const NOTIFY_TTL_MAX_MINUTES = 24 * 60;

const PLACEHOLDER_RE = /\{([^{}]+)\}/g;

/** מחליף {שם} בערך המתאים; placeholder לא מוכר נמחק (ההגדרות מזהירות על כאלה לפני שמירה) */
export function renderNotifyText(template: string, values: Record<string, string>): string {
  return template
    .replace(PLACEHOLDER_RE, (_m, key: string) => values[key.trim()] ?? '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/** שמות ה-placeholders שמופיעים בתבנית ואינם ברשימה המותרת — להצגת אזהרה בהגדרות */
export function unknownPlaceholders(template: string, allowed: readonly string[]): string[] {
  const unknown = new Set<string>();
  for (const m of template.matchAll(PLACEHOLDER_RE)) {
    const key = m[1]!.trim();
    if (!allowed.includes(key)) unknown.add(key);
  }
  return [...unknown];
}

const FALLBACK_NOTIFY_TITLE = 'מבצע בר-מצווש';
const FALLBACK_NOTIFY_BODY = 'יש עדכון חדש — היכנסו לאפליקציה';

/**
 * התוכן הסופי של התראה מתבנית: הצבת הערכים בכותרת ובגוף. תוצאה ריקה (למשל תבנית שכולה placeholder בלי ערך)
 * מוחלפת בברירת מחדל, כדי שהתראה לעולם לא תגיע ריקה. אותה פונקציה משמשת את השרת ואת התצוגה המקדימה בהגדרות.
 */
export function renderNotifyContent(
  template: Pick<NotifyTemplate, 'title' | 'body'>,
  values: Record<string, string>,
): { title: string; body: string } {
  return {
    title: renderNotifyText(template.title, values) || values['שם המבצע'] || FALLBACK_NOTIFY_TITLE,
    body: renderNotifyText(template.body, values) || FALLBACK_NOTIFY_BODY,
  };
}

/** ערכי דוגמה לתבניות — לתצוגה המקדימה בהגדרות ולהתראת הבדיקה של תבנית */
export function sampleNotifyValues(operationName: string): Record<string, string> {
  return {
    'שם המבצע': operationName || 'מבצע בר-מצווש',
    משימה: 'משימה #11',
    מספר: '11',
    הודעה: 'זו הודעה לדוגמה מהמפקדה',
    הערה: 'התמונה מטושטשת — צלמו שוב מקרוב',
  };
}

/** סימון הדחיפות בכותרת ההתראה: 🚨 דחופה, ⏰ רגישה בזמן */
export function markTitle(title: string, flags: { urgent: boolean; timeSensitive: boolean }): string {
  const marks = `${flags.urgent ? '🚨' : ''}${flags.timeSensitive ? '⏰' : ''}`;
  return marks ? `${marks} ${title}`.trim() : title;
}

/** תוקף ההתראה בשניות לפי ההגדרה: רגישה בזמן = ttlMinutes, אחרת יום שלם */
export function deliveryFor(template: Pick<NotifyTemplate, 'ttlMinutes'>, timeSensitive: boolean): PushDelivery {
  const minutes = Math.min(NOTIFY_TTL_MAX_MINUTES, Math.max(NOTIFY_TTL_MIN_MINUTES, Math.round(template.ttlMinutes)));
  return { ttlSeconds: timeSensitive ? minutes * 60 : DEFAULT_PUSH_TTL_SECONDS };
}

/** מאחד תבניות שנשמרו (חלקיות/ישנות) עם ברירות המחדל */
export function mergeNotifyTemplates(saved: Partial<Record<NotifyEventKey, Partial<NotifyTemplate>>> | null | undefined): NotifyTemplates {
  const out = {} as NotifyTemplates;
  for (const key of Object.keys(DEFAULT_NOTIFY_TEMPLATES) as NotifyEventKey[]) {
    out[key] = { ...DEFAULT_NOTIFY_TEMPLATES[key], ...(saved?.[key] ?? {}) };
  }
  return out;
}

/** מנוי Push כפי שהדפדפן מייצר (PushSubscription.toJSON) */
export interface PushSubscriptionInput {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export interface PushKeyDTO {
  publicKey: string;
}

export interface PushStatusDTO {
  /** כמות מכשירים רשומים לכל תפקיד */
  player: number;
  admin: number;
}

export interface PushTestResult {
  sent: number;
  removed: number;
  failed: number;
}

/**
 * שירותי ה-Push של הדפדפנים היחידים שהשרת מוכן לפנות אליהם. בלי הרשימה הזו כל מי שרשום "מנוי" עם כתובת
 * פנימית כלשהי היה גורם לשרת לשלוח אליה בקשות (SSRF).
 */
const ALLOWED_PUSH_HOST_SUFFIXES = [
  'fcm.googleapis.com',
  'android.googleapis.com',
  'push.services.mozilla.com',
  'push.apple.com',
  'notify.windows.com',
];

export function isAllowedPushEndpoint(endpoint: string): boolean {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.port) return false;
  const host = url.hostname.toLowerCase();
  return ALLOWED_PUSH_HOST_SUFFIXES.some((s) => host === s || host.endsWith(`.${s}`));
}

/** בסיס64 בטוח ל-URL → Uint8Array (לדרישת applicationServerKey של הדפדפן) */
export function base64UrlToBytes(value: string): Uint8Array {
  const padded = value + '='.repeat((4 - (value.length % 4)) % 4);
  const b64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
